import { TextInput } from '@/components/ui/text-input';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  Platform,
  Pressable,
  Text,
  View
} from 'react-native';
import { useAdminCreateInvite } from '../hooks/use-admin-invites';

interface AdminCreateInviteModalProps {
  visible: boolean;
  onClose: () => void;
}

const EXPIRY_OPTIONS = [
  { label: '7 days', value: 7 },
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
  { label: 'Never', value: undefined },
];

export function AdminCreateInviteModal({ visible, onClose }: AdminCreateInviteModalProps) {
  const [email, setEmail] = useState('');
  const [expiryIndex, setExpiryIndex] = useState(1);
  const [createdCode, setCreatedCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const createMutation = useAdminCreateInvite();

  const handleCreate = () => {
    createMutation.mutate(
      {
        email: email.trim() || undefined,
        expiresInDays: EXPIRY_OPTIONS[expiryIndex].value,
      },
      {
        onSuccess: (data) => {
          setCreatedCode(data.code);
        },
      },
    );
  };

  const handleCopy = async () => {
    if (!createdCode) return;
    try {
      if (Platform.OS === 'web' && navigator.clipboard) {
        await navigator.clipboard.writeText(createdCode);
      }
    } catch {
      // Ignore clipboard errors
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    setEmail('');
    setExpiryIndex(1);
    setCreatedCode(null);
    setCopied(false);
    createMutation.reset();
    onClose();
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={handleClose}>
      <Pressable
        style={{
          flex: 1,
          backgroundColor: 'rgba(0,0,0,0.5)',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 24,
        }}
        onPress={handleClose}
      >
        <Pressable
          onPress={(e) => e.stopPropagation()}
          className="bg-background"
          style={{
            borderRadius: 16,
            padding: 24,
            width: '100%',
            maxWidth: 400,
          }}
        >
          <Text className="text-foreground" style={{ fontSize: 18, fontWeight: '700', marginBottom: 16 }}>
            Create Invite Code
          </Text>

          {createdCode ? (
            <View>
              <Text className="text-muted-foreground" style={{ fontSize: 14, marginBottom: 12 }}>
                Invite code created successfully:
              </Text>
              <Pressable
                onPress={handleCopy}
                accessibilityRole="button"
                accessibilityLabel="Copy invite code to clipboard"
                className="bg-surface"
                style={{
                  borderRadius: 10,
                  padding: 16,
                  alignItems: 'center',
                  marginBottom: 16,
                }}
              >
                <Text
                  className="text-foreground"
                  style={{
                    fontSize: 22,
                    fontWeight: '700',
                    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
                    letterSpacing: 2,
                    marginBottom: 4,
                  }}
                >
                  {createdCode}
                </Text>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                  <Ionicons
                    name={copied ? 'checkmark' : 'copy-outline'}
                    size={14}
                    className="text-primary"
                  />
                  <Text
                    className="text-primary"
                    style={{ fontSize: 12 }}
                    accessibilityLiveRegion="polite"
                  >
                    {copied ? 'Copied!' : 'Tap to copy'}
                  </Text>
                </View>
              </Pressable>
              <Pressable
                onPress={handleClose}
                className="bg-primary"
                style={{
                  borderRadius: 10,
                  paddingVertical: 12,
                  alignItems: 'center',
                }}
              >
                <Text className="text-background" style={{ fontSize: 15, fontWeight: '600' }}>
                  Done
                </Text>
              </Pressable>
            </View>
          ) : (
            <View>
              <TextInput
                label="Email (optional)"
                containerClassName="mb-4"
                value={email}
                onChangeText={setEmail}
                placeholder="recipient@example.com"
                autoCapitalize="none"
                autoCorrect={false}
                keyboardType="email-address"
              />

              <Text className="text-foreground" style={{ fontSize: 13, fontWeight: '600', marginBottom: 6 }}>
                Expiration
              </Text>
              <View style={{ flexDirection: 'row', gap: 8, marginBottom: 20 }}>
                {EXPIRY_OPTIONS.map((opt, idx) => (
                  <Pressable
                    key={opt.label}
                    onPress={() => setExpiryIndex(idx)}
                    accessibilityRole="button"
                    accessibilityState={{ selected: idx === expiryIndex }}
                    className={idx === expiryIndex ? 'bg-primary' : 'bg-surface'}
                    style={{
                      flex: 1,
                      paddingVertical: 8,
                      borderRadius: 8,
                      alignItems: 'center',
                    }}
                  >
                    <Text
                      className={idx === expiryIndex ? 'text-background' : 'text-foreground'}
                      style={{ fontSize: 13, fontWeight: '600' }}
                    >
                      {opt.label}
                    </Text>
                  </Pressable>
                ))}
              </View>

              <View style={{ flexDirection: 'row', gap: 10 }}>
                <Pressable
                  onPress={handleClose}
                  className="bg-surface-secondary"
                  style={{
                    flex: 1,
                    borderRadius: 10,
                    paddingVertical: 12,
                    alignItems: 'center',
                  }}
                >
                  <Text className="text-foreground" style={{ fontSize: 15, fontWeight: '600' }}>
                    Cancel
                  </Text>
                </Pressable>
                <Pressable
                  onPress={handleCreate}
                  disabled={createMutation.isPending}
                  className="bg-primary"
                  style={{
                    flex: 1,
                    borderRadius: 10,
                    paddingVertical: 12,
                    alignItems: 'center',
                    opacity: createMutation.isPending ? 0.6 : 1,
                  }}
                >
                  {createMutation.isPending ? (
                    <ActivityIndicator className="text-background" />
                  ) : (
                    <Text className="text-background" style={{ fontSize: 15, fontWeight: '600' }}>
                      Create
                    </Text>
                  )}
                </Pressable>
              </View>
            </View>
          )}
        </Pressable>
      </Pressable>
    </Modal>
  );
}
